/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useContext, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Dropdown } from "@carbon/react";
import Button from "../../buttons/Button";
import { errorAlert, makeApiRequest, successAlert } from "../../../util";
import AuthContext from "../../../store/authContext";
import { ADD_DFSI_GROUP_FORM_FIELDS } from "../../../constants";
import Form from "../../../ui/Form/Form";
import TextField from "../../inputs/TextField";
import { IDFSIGroupData } from "../../../types";
import DeleteModal, { DeleteModalIcons } from "../../modal/DeleteModal";
import DropdownSearchField from "../../inputs/DropdownSearchField";
import MultiselectSearchField from "../../inputs/MultiselectSearchField";
import {
  CREATE_DFSI_GROUP_TRANSLATIONS,
  FORM_VALIDATION_TRANSLATIONS,
  HEX_INPUT_TRANSLATIONS,
} from "../../../translations/util";
import ToggleField from "../../inputs/ToggleField";
import NumberField from "../../inputs/NumberField";

interface IProps {
  org: string;
  iwfId: string;
}
const set = new Set();

function isAlphanumeric(str: string) {
  return /^[a-fA-F0-9]+$/.test(str);
}

const isFormInvalid = ({ data }: { data: any }) => {
  if (!data) return true;
  if (!data["IWF-ID"]) return true;
  if (!data.FORMAT) return true;
  if (!data["DFSI-LOGICAL-GID"]) return true;
  if (!data["DFSI-GROUP-NAME"]) return true;
  if (data["ENCRYPTION-ENABLED"] && !data.SLN && data.SLN !== 0) return true;
  if (data.SLN < 0 || data.SLN > 65535) return true;
  if (!data["CHANNEL-IDS"] || data["CHANNEL-IDS"].length < 1) return true;
  if (
    data.FORMAT === "PreFixed" &&
    (data["DFSI-LOGICAL-GID"].length !== 4 ||
      !isAlphanumeric(data["DFSI-LOGICAL-GID"]))
  )
    return true;

  return false;
};

const NewDFSIGroup = ({ org, iwfId }: IProps) => {
  const { token } = useContext(AuthContext);
  const [loading, setLoading] = useState(false);
  const [channelIds, setChannelIds] = useState<{ id: string; text: string }[]>(
    []
  );
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  useEffect(() => {
    set.clear();
    return () => {
      set.clear();
    };
  }, []);

  const [connectionData, setConnectionData] = useState<IDFSIGroupData>({
    "IWF-ID": iwfId,
    FORMAT: "PreFixed",
    "DFSI-LOGICAL-GID": "",
    "CHANNEL-IDS": "",
    "DFSI-GROUP-NAME": "",
    "ENCRYPTION-ENABLED": false,
    SLN: undefined,
  });

  const createDFSIGroup = async () => {
    setLoading(true);
    try {
      await makeApiRequest<{ Ref: string }>({
        path: `/api/v1/app-config/dfsi-groups`,
        method: "POST",
        body: JSON.stringify({
          "IWF-ID": connectionData["IWF-ID"],
          "DFSI-LOGICAL-GID":
            connectionData.FORMAT === "FreeForm"
              ? connectionData["DFSI-LOGICAL-GID"]
              : `${org}-${connectionData["IWF-ID"]}${connectionData["DFSI-LOGICAL-GID"]}`,
          "CHANNEL-IDS": connectionData["CHANNEL-IDS"],
          "DFSI-GROUP-NAME": connectionData["DFSI-GROUP-NAME"],
          "ENCRYPTION-ENABLED": connectionData["ENCRYPTION-ENABLED"],
          ...(connectionData &&
            connectionData["ENCRYPTION-ENABLED"] && {
              SLN: connectionData.SLN,
            }),
        }),
        token,
        org,
      });
      successAlert(CREATE_DFSI_GROUP_TRANSLATIONS.successAlertMessage);
      navigate("/configurationManagement/dfsiGroups");
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (e: any) {
      errorAlert(e);
    } finally {
      setLoading(false);
    }
  };

  const getDFSIChannelIds = async (orgId: string) => {
    try {
      const response = await makeApiRequest<{
        Rows: { "CHANNEL-ID": string }[];
      }>({
        path: `/api/v1/app-config/dfsi-channels/ids/`,
        token,
        org: orgId,
      });
      setChannelIds(
        response.Rows.map((row) => ({
          id: row["CHANNEL-ID"],
          text: row["CHANNEL-ID"],
        }))
      );
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (e: any) {
      errorAlert(e);
    }
  };

  useEffect(() => {
    getDFSIChannelIds(org);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [org]);

  return (
    <>
      <div className="data-form">
        <Form>
          <div className="data-tabcontent data-card">
            {ADD_DFSI_GROUP_FORM_FIELDS.map(
              (rows: {
                cols: number;
                fields: {
                  name: keyof IDFSIGroupData;
                  tooltipText?: string;
                  label: string;
                  type: string;
                  invalidText?: string | null;
                  isInputInvalid?: (val?: string | number) => boolean;
                  min?: number;
                  max?: number;
                  items?: string[];
                  disabled?: boolean;
                }[];
              }) => {
                return (
                  <div
                    className={`grid grid-cols-${rows.cols} gap-4 form-control single-item`}
                  >
                    {rows.fields.map((field) => {
                      switch (field.type) {
                        case "TOGGLE":
                          return (
                            <ToggleField
                              labelText={field.label}
                              onChange={(val) =>
                                setConnectionData({
                                  ...connectionData,
                                  [field.name]: val,
                                })
                              }
                              value={connectionData[field.name] as boolean}
                              tooltipText={field.tooltipText}
                              defaultToggled
                            />
                          );
                        case "MULTISELECT":
                          return (
                            <MultiselectSearchField
                              titleText={field.label}
                              placeholder={field.label}
                              items={channelIds}
                              tooltipText={field.tooltipText}
                              onChange={({
                                selectedItems,
                              }: {
                                selectedItems: { id: string; text: string }[];
                              }) =>
                                setConnectionData({
                                  ...connectionData,
                                  [field.name]: selectedItems.map((item) => ({
                                    "CHANNEL-ID": item.id,
                                  })),
                                })
                              }
                              required
                            />
                          );
                        case "COMBO-DROPDOWN":
                          return (
                            <DropdownSearchField
                              className="dropdown-field"
                              label={field.label}
                              placeholder={field.label}
                              items={field.items || []}
                              onChange={({
                                selectedItem,
                              }: {
                                selectedItem: string;
                              }) =>
                                setConnectionData({
                                  ...connectionData,
                                  [field.name]: selectedItem,
                                })
                              }
                              selectedItem={connectionData[field.name]}
                              tooltipText={field.tooltipText}
                              required
                            />
                          );
                        case "TEXT-CUSTOM":
                          return connectionData.FORMAT !== "PreFixed" ? (
                            <TextField
                              onChange={(val) => {
                                set.add(field.name);
                                setConnectionData({
                                  ...connectionData,
                                  [field.name]: val,
                                });
                              }}
                              placeholder={field.label}
                              value={connectionData[field.name] as string}
                              invalid={
                                set.has(field.name) &&
                                field.isInputInvalid?.(
                                  connectionData[field.name] as string
                                )
                              }
                              invalidText={
                                field.invalidText ||
                                FORM_VALIDATION_TRANSLATIONS.required(
                                  field.label
                                )
                              }
                              labelText={field.label}
                              tooltipText={field.tooltipText}
                              required
                              disabled={field.disabled}
                            />
                          ) : (
                            <div className="hexdec-block">
                              <div className="input-wrapper">
                                <Dropdown
                                  className="input-dropdown"
                                  items={[
                                    HEX_INPUT_TRANSLATIONS.HEX,
                                    HEX_INPUT_TRANSLATIONS.DEC,
                                  ]}
                                  disabled
                                  selectedItem={HEX_INPUT_TRANSLATIONS.HEX}
                                />
                                <div className="input-field">
                                  <TextField
                                    onChange={(val) => {
                                      set.add(field.name);
                                      setConnectionData({
                                        ...connectionData,
                                        [field.name]: val,
                                      });
                                    }}
                                    placeholder={field.label}
                                    value={connectionData[field.name] as string}
                                    invalid={
                                      set.has(field.name) &&
                                      (connectionData[field.name]?.toString()
                                        ?.length !== 4 ||
                                        !isAlphanumeric(
                                          connectionData[
                                            field.name
                                          ]?.toString() ?? ""
                                        ))
                                    }
                                    invalidText={
                                      field.invalidText ||
                                      FORM_VALIDATION_TRANSLATIONS.characterValidation(
                                        field.label
                                      )
                                    }
                                    labelText={field.label}
                                    tooltipText={field.tooltipText}
                                    required
                                    disabled={field.disabled}
                                  />
                                </div>
                              </div>
                            </div>
                          );
                        case "NUMBER":
                          return (
                            <NumberField
                              onChange={(val) => {
                                set.add(field.name);
                                if (!val || !Number.isNaN(+val))
                                  setConnectionData({
                                    ...connectionData,
                                    [field.name]: val ? +val : undefined,
                                  });
                              }}
                              label={field.label}
                              placeholder={field.label}
                              max={field.max || 0}
                              min={field.min || 0}
                              invalid={
                                set.has(field.name) &&
                                connectionData[field.name] !== 0 &&
                                !connectionData[field.name]
                              }
                              value={connectionData[field.name] as number}
                              invalidText={
                                field.invalidText ||
                                `"${field.label}" ${FORM_VALIDATION_TRANSLATIONS.range} (${field.min}-${field.max})`
                              }
                              tooltipText={field.tooltipText}
                              disabled={!connectionData["ENCRYPTION-ENABLED"]}
                              required
                            />
                          );
                        default:
                          return (
                            <TextField
                              onChange={(val) => {
                                set.add(field.name);
                                setConnectionData({
                                  ...connectionData,
                                  [field.name]: val,
                                });
                              }}
                              placeholder={field.label}
                              value={connectionData[field.name] as string}
                              invalid={
                                set.has(field.name) &&
                                field.isInputInvalid?.(
                                  connectionData[field.name] as string
                                )
                              }
                              invalidText={
                                field.invalidText ||
                                FORM_VALIDATION_TRANSLATIONS.required(
                                  field.label
                                )
                              }
                              labelText={field.label}
                              tooltipText={field.tooltipText}
                              required
                              disabled={field.disabled}
                            />
                          );
                      }
                    })}
                  </div>
                );
              }
            )}
          </div>
        </Form>
      </div>
      <DeleteModal
        modalHeaderText={CREATE_DFSI_GROUP_TRANSLATIONS.deleteModal.header}
        modalDescriptionText={
          CREATE_DFSI_GROUP_TRANSLATIONS.deleteModal.description
        }
        onDelete={() => navigate("/configurationManagement/dfsiGroups")}
        open={open}
        setOpen={setOpen}
        confirmationButtonLabel={
          CREATE_DFSI_GROUP_TRANSLATIONS.deleteModal.goBackButtonLabel
        }
        cancelIcon={DeleteModalIcons.Cancel}
      />
      <div className="flex justify-end form-footer">
        <div className="mr-2">
          <Button
            className="w-full"
            type="button"
            kind="tertiary"
            disabled={false}
            label={CREATE_DFSI_GROUP_TRANSLATIONS.cancelButtonLabel}
            onClick={() => setOpen(true)}
            dataCy="cancelCreateDFSIGroup-"
          />
        </div>

        <div>
          <Button
            className="w-full"
            type="button"
            loading={loading}
            disabled={isFormInvalid({ data: connectionData })}
            onClick={createDFSIGroup}
            label={CREATE_DFSI_GROUP_TRANSLATIONS.createButtonLabel}
            dataCy="createDFSIGroup-"
          />
        </div>
      </div>
    </>
  );
};

export default NewDFSIGroup;
